import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { API_BASE_URL } from "../../utils/constants";
import { readErrorMessage } from "../../utils/apiError";
import { authUrl } from "../../utils/auth";
import AddArbitrator from "./AddArbitrator";
import "./Arbitrator.css";

// Arbitrators: list (active / inactive), add, edit, deactivate / reactivate. Nothing is ever erased:
// cases keep pointing to their arbitrator, so "deactivate" only hides the arbitrator from new case assignment.

const toastError = (message) => toast.error(message, { theme: "colored", autoClose: 5000 });
const toastSuccess = (message) => toast.success(message, { theme: "colored", autoClose: 2500 });

// How the name block prints in letters, e.g. "Adv. Suresh S. Kasture, LL.B. (Retd. Addl. Public Prosecutor…)"
export const printsAs = (a) => [a.arbName, [a.education, a.designation].filter(Boolean).join(" ")].filter(Boolean).join(", ");

const initials = (name) =>
  String(name || "")
    .replace(/^(adv|mr|mrs|ms|dr|shri|smt)\.?\s+/i, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

const Photo = ({ arbitrator, version }) => {
  const [failed, setFailed] = useState(false);
  if (!arbitrator.pictures.photo || failed) {
    return (
      <div
        className="d-flex align-items-center justify-content-center rounded-circle"
        style={{ width: 72, height: 72, background: "#e9ecef", color: "#495057", fontWeight: 700, fontSize: 22 }}
      >
        {initials(arbitrator.arbName)}
      </div>
    );
  }
  return (
    <img
      src={authUrl(`${API_BASE_URL}/api/arbitrator/${arbitrator.arbId}/picture/photo?v=${version}`)}
      onError={() => setFailed(true)}
      alt={arbitrator.arbName}
      className="rounded-circle"
      style={{ width: 72, height: 72, objectFit: "cover" }}
    />
  );
};

const Badge = ({ color, children, title }) => (
  <span title={title} style={{ background: color, color: "#fff", padding: "2px 8px", borderRadius: 10, fontSize: "0.72rem", whiteSpace: "nowrap" }}>
    {children}
  </span>
);

const Arbitrator = () => {
  const [arbitrators, setArbitrators] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [filter, setFilter] = useState("active");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // null = list; {} = new; record = edit
  const [version, setVersion] = useState(Date.now()); // refreshes pictures after a save
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/arbitrators/manage`);
      if (!res.ok) throw new Error(await readErrorMessage(res));
      setArbitrators(await res.json());
      setVersion(Date.now());
    } catch (e) {
      setLoadError(e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (arbitrators || [])
      .filter((a) => (filter === "all" ? true : filter === "active" ? a.active : !a.active))
      .filter((a) => !term || [a.arbName, a.firmName, a.emailId, a.contactNo, ...a.logins].join(" ").toLowerCase().includes(term));
  }, [arbitrators, filter, search]);

  const counts = useMemo(
    () => ({
      active: (arbitrators || []).filter((a) => a.active).length,
      inactive: (arbitrators || []).filter((a) => !a.active).length,
    }),
    [arbitrators],
  );

  const setStatus = async (arbitrator, active) => {
    if (
      !active &&
      !window.confirm(
        `Deactivate ${arbitrator.arbName}?\n\nThey will no longer be offered when assigning cases. Nothing is deleted, and they can be reactivated later.`,
      )
    ) {
      return;
    }
    setBusyId(arbitrator.arbId);
    try {
      const send = (confirmOpenCases) =>
        fetch(`${API_BASE_URL}/api/arbitrators/${arbitrator.arbId}/status`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ active, confirmOpenCases }),
        });
      let res = await send(false);
      if (res.status === 409) {
        const message = await readErrorMessage(res);
        if (!window.confirm(`${message}\n\nDeactivate anyway?`)) return;
        res = await send(true);
      }
      if (!res.ok) throw new Error(await readErrorMessage(res));
      toastSuccess(`${arbitrator.arbName} ${active ? "reactivated" : "deactivated"}`);
      await load();
    } catch (e) {
      toastError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  if (editing) {
    return (
      <div className="container mt-4">
        <AddArbitrator
          initialData={editing.arbId ? editing : null}
          version={version}
          onCancel={() => setEditing(null)}
          onSave={async (saved) => {
            toastSuccess(`${saved?.arbName || "Arbitrator"} saved`);
            setEditing(null);
            await load();
          }}
        />
        <ToastContainer />
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
        <div>
          <h5 className="mb-1">Arbitrators</h5>
          <small className="text-muted">
            The name, qualification, description, experience, chamber address and signature here are printed on the case
            letters.
          </small>
        </div>
        <button className="custBtn px-4" style={{ fontSize: "0.85rem", padding: "6px 14px" }} onClick={() => setEditing({})}>
          + Add Arbitrator
        </button>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <div className="btn-group btn-group-sm">
          {[
            ["active", `Active (${counts.active})`],
            ["inactive", `Inactive (${counts.inactive})`],
            ["all", "All"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`btn ${filter === key ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          className="form-control form-control-sm"
          style={{ maxWidth: 300 }}
          placeholder="Search name, firm, email, phone or login…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loadError && (
        <div className="alert alert-danger py-2">
          Could not load the arbitrators: {loadError}{" "}
          <button className="btn btn-link btn-sm p-0" onClick={load}>
            Try again
          </button>
        </div>
      )}
      {arbitrators === null && !loadError && <div className="text-muted">Loading…</div>}
      {arbitrators && shown.length === 0 && (
        <div className="text-muted py-4 text-center">{search ? "No arbitrator matches your search." : "No arbitrators here."}</div>
      )}

      <div className="d-flex flex-column gap-3">
        {shown.map((a) => (
          <div key={a.arbId} className="border rounded customShadow p-3" style={{ opacity: a.active ? 1 : 0.75 }}>
            <div className="d-flex gap-3">
              <Photo arbitrator={a} version={version} />
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <h6 className="m-0 fw-bold">{a.arbName}</h6>
                  {a.active ? <Badge color="#198754">Active</Badge> : <Badge color="#6c757d">Inactive</Badge>}
                  {a.missing.map((m) => (
                    <Badge key={m} color="#dc3545" title="Letters need this detail">
                      No {m.toLowerCase()}
                    </Badge>
                  ))}
                </div>
                <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                  Prints as: <em>{printsAs(a)}</em>
                </div>
                <div className="row mt-2" style={{ fontSize: "0.8rem" }}>
                  <div className="col-md-5">
                    <div>Chamber: {a.address || <span className="text-danger">not set</span>}</div>
                    <div>
                      Phone: {a.contactNo || "—"} · Email: {a.emailId || "—"}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div>
                      Cases: <strong>{a.openCases}</strong> open of {a.totalCases}
                    </div>
                    <div>
                      Login:{" "}
                      {a.logins.length ? (
                        a.logins.join(", ")
                      ) : (
                        <span className="text-muted">no login linked</span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-3 d-flex gap-1 justify-content-md-end align-items-start mt-2 mt-md-0">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => setEditing(a)} disabled={busyId === a.arbId}>
                      Edit
                    </button>
                    <button
                      className={`btn btn-sm ${a.active ? "btn-outline-warning" : "btn-outline-success"}`}
                      onClick={() => setStatus(a, !a.active)}
                      disabled={busyId === a.arbId}
                    >
                      {busyId === a.arbId ? "…" : a.active ? "Deactivate" : "Reactivate"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <ToastContainer />
    </div>
  );
};

export default Arbitrator;
